import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Flag, BookOpen, BarChart3, Settings, Home, ArrowLeft } from 'lucide-react';
import { ThemeToggle } from '@/components/common';
import { useQuizProgressStore } from '@/store';
import { cn } from '@/lib/utils';

const navItems = [
  { path: '/', icon: Home, label: 'Home' },
  { path: '/learn', icon: BookOpen, label: 'Learn' },
  { path: '/stats', icon: BarChart3, label: 'Stats' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

export function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const isQuizPage = location.pathname === '/quiz';
  const { current, total, correctCount, isActive } = useQuizProgressStore();
  const progress = total > 0 ? (current / total) * 100 : 0;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-stone-900/90 backdrop-blur-sm border-b border-stone-200 dark:border-stone-800">
        {isQuizPage ? (
          <div className="max-w-3xl mx-auto px-4">
            <div className="h-14 flex items-center justify-between">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-1.5 text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Exit</span>
              </button>
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-primary-500 rounded">
                  <Flag className="w-4 h-4 text-white" />
                </div>
                <span className="text-lg font-semibold text-stone-900 dark:text-stone-100" style={{ fontFamily: 'var(--font-display)' }}>
                  Flag Trainer
                </span>
              </div>
              <ThemeToggle />
            </div>
            {isActive && (
              <div className="pb-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-500">
                    Question {current} of {total}
                  </span>
                  <span className="font-medium text-success-600 dark:text-success-400">
                    {correctCount} correct
                  </span>
                </div>
                <div className="h-2 bg-stone-200 dark:bg-stone-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="p-1.5 bg-primary-500 rounded">
                <Flag className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-semibold text-stone-900 dark:text-stone-100" style={{ fontFamily: 'var(--font-display)' }}>
                Flag Trainer
              </span>
            </Link>
            <div className="flex items-center gap-1">
              <nav className="hidden md:flex items-center gap-1 mr-2">
                {navItems.map(({ path, icon: Icon, label }) => {
                  const isActive = location.pathname === path;
                  return (
                    <Link
                      key={path}
                      to={path}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800'
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </Link>
                  );
                })}
              </nav>
              <ThemeToggle />
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-6">
        <Outlet />
      </main>

      {/* Bottom navigation (mobile) */}
      <nav className="sticky bottom-0 bg-white dark:bg-stone-900 border-t border-stone-200 dark:border-stone-800 md:hidden">
        <div className="flex justify-around items-center h-14">
          {navItems.map(({ path, icon: Icon, label }) => {
            const isActive = location.pathname === path;
            return (
              <Link
                key={path}
                to={path}
                className={cn(
                  'flex flex-col items-center gap-0.5 px-4 py-2 transition-colors',
                  isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-stone-500 hover:text-stone-700 dark:hover:text-stone-300'
                )}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs font-medium">{label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
